import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { badge, box, input, label } from '../shared/styles';
import Form from '../iso/Form';
import AiModelDashboard from '../../components/AiModelDashboard';
import AiThresholdsPanel from '../../components/AiThresholdsPanel';
import ServedModelCard from '../../components/ServedModelCard';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import { useAuth } from '../../context/AuthContext';
import { can } from '../../utils/permissions';

// mlops:read reads every tab; only mlops:admin builds, freezes, uploads,
// trains, promotes, archives and rolls back (routes/mlops.py). The read-only
// executive reads.
const MayRun = createContext(true);
const WriteForm = (props) => (useContext(MayRun) ? <Form {...props} /> : null);

/*
 * MLOps (Task 6.8) -- system administrators only; the server enforces it.
 * Datasets are built from the lab's labelled photos and frozen before any
 * training; training runs on the lab GPU host; a candidate becomes production
 * only if it matches or beats production on the same frozen test split, and
 * rollback returns to the previous production model. Every step asks why.
 */
const pct = (v) => (v === null || v === undefined ? '—' : `${(Number(v) * 100).toFixed(1)}%`);
const STAGE_TONE = { production: 'success', candidate: 'warning', archived: 'info' };
const RUN_TONE = { queued: 'info', running: 'warning', succeeded: 'success', failed: 'danger', cancelled: 'info' };

/*
 * The pipeline at a glance: five steps in order, each with what it does, what
 * it needs, what moves it on, and where it stands now. Nothing here acts; a
 * step opens the tab where it is done.
 */
export const STEPS = [
  { key: 'build', tab: 'datasets' },
  { key: 'freeze', tab: 'datasets' },
  { key: 'train', tab: 'training' },
  { key: 'gate', tab: 'models' },
  { key: 'promote', tab: 'models' },
];

export function pipelineStatus({ datasets = [], versions = [], runs = [], models = [] }) {
  const count = (list, pred) => list.filter(pred).length;
  return {
    build: { datasets: datasets.length, built: count(versions, (v) => v.status === 'built') },
    freeze: { frozen: count(versions, (v) => v.status === 'frozen') },
    train: {
      queued: count(runs, (r) => r.status === 'queued'),
      running: count(runs, (r) => r.status === 'running'),
      succeeded: count(runs, (r) => r.status === 'succeeded'),
      failed: count(runs, (r) => r.status === 'failed'),
    },
    gate: { candidates: count(models, (m) => m.stage === 'candidate') },
    promote: { production: (models.find((m) => m.stage === 'production') || {}).model_version || null },
  };
}

function Pipeline({ t, onOpen }) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [d, r, m] = await Promise.all([api.get('/mlops/datasets'), api.get('/mlops/training-runs'),
          api.get('/mlops/models')]);
        const details = await Promise.all((d.data || []).map((x) => api.get(`/mlops/datasets/${x.dataset_id}`)));
        const versions = details.flatMap(({ data }) => data.versions || []);
        if (live) setStatus(pipelineStatus({ datasets: d.data, versions, runs: r.data, models: m.data }));
      } catch {
        if (live) setStatus(pipelineStatus({}));
      }
    })();
    return () => { live = false; };
  }, []);
  const summary = (key) => {
    if (!status) return t('pipeline.loading');
    const s = status[key];
    if (key === 'build') return t('pipeline.status_build', { datasets: s.datasets, built: s.built });
    if (key === 'freeze') return t('pipeline.status_freeze', { frozen: s.frozen });
    if (key === 'train') return t('pipeline.status_train', s);
    if (key === 'gate') return t('pipeline.status_gate', { candidates: s.candidates });
    return s.production ? t('pipeline.status_promote', { version: s.production }) : t('pipeline.status_noProduction');
  };
  return (
    <section style={{ ...box, marginBottom: 16 }} data-testid="pipeline">
      <h3 style={{ marginTop: 0 }}>{t('pipeline.title')}</h3>
      <ol style={{ margin: 0, paddingInlineStart: 20, display: 'grid', gap: 10 }}>
        {STEPS.map((step, i) => (
          <li key={step.key} data-testid={`step-${step.key}`}>
            <button type="button" onClick={() => onOpen(step.tab)} title={t(`pipeline.${step.key}.tip`)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 600, color: 'var(--c-primary-fg)' }}>
              {i + 1}. {t(`pipeline.${step.key}.title`)}
            </button>
            <div style={{ fontSize: 13 }}>{t(`pipeline.${step.key}.what`)}</div>
            <div style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>
              {t('pipeline.needs')}: {t(`pipeline.${step.key}.needs`)} · {t('pipeline.next')}: {t(`pipeline.${step.key}.next`)}
            </div>
            <div style={{ fontSize: 12 }} data-testid={`step-status-${step.key}`}><strong>{summary(step.key)}</strong></div>
          </li>
        ))}
      </ol>
      <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', marginBottom: 0 }}>{t('pipeline.triggers')}</p>
    </section>
  );
}

function Datasets({ t }) {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  // Uploaded sets (the Uploads tab) a build may include; none by default.
  const [uploads, setUploads] = useState([]);
  const [chosenUploads, setChosenUploads] = useState([]);
  const load = useCallback(() => api.get('/mlops/datasets').then(({ data }) => setList(data))
    .catch((e) => setError(errorText(e))), []);
  const open = useCallback((id) => api.get(`/mlops/datasets/${id}`).then(({ data }) => setSelected(data))
    .catch((e) => setError(errorText(e))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/mlops/uploads').then(({ data }) => setUploads(Array.isArray(data) ? data : [])).catch(() => setUploads([]));
  }, []);
  const toggleUpload = (id) => setChosenUploads((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const create = async (v) => { const { data } = await api.post('/mlops/datasets', { ...v, task: 'classification' }); await load(); await open(data.dataset_id); };
  const build = async (v) => {
    await api.post(`/mlops/datasets/${selected.dataset_id}/versions`, {
      split_seed: Number(v.split_seed), reason: v.reason,
      source_query: {
        sources: [v.expert && 'expert_identification', v.ai_review && 'ai_review',
          chosenUploads.length > 0 && 'upload'].filter(Boolean),
        rank: v.rank, min_agreement: Number(v.min_agreement), min_per_class: Number(v.min_per_class),
        val_fraction: Number(v.val_fraction), test_fraction: Number(v.test_fraction),
        ...(chosenUploads.length > 0 ? { upload_ids: chosenUploads } : {}),
      },
    });
    setChosenUploads([]);
    await open(selected.dataset_id);
  };
  const freeze = (versionId) => async (v) => {
    await api.post(`/mlops/datasets/${selected.dataset_id}/versions/${versionId}/freeze`, v);
    await open(selected.dataset_id);
  };

  return (
    <div>
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      <section style={box}>
        <h4 style={{ marginTop: 0 }}>{t('newDataset')}</h4>
        <WriteForm t={t} testId="dataset-form" submitLabel="create" onSubmit={create}
          fields={[{ name: 'name' }, { name: 'description' }, { name: 'reason' }]} />
      </section>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <ul style={{ ...box, listStyle: 'none', minWidth: 220, flex: '0 1 260px', margin: 0 }}>
          {list.length === 0 && <li>{t('noDatasets')}</li>}
          {list.map((d) => (
            <li key={d.dataset_id}><button type="button" data-testid={`dataset-${d.name}`} onClick={() => open(d.dataset_id)} title={t('openDatasetTip')}
              style={{ background: 'none', border: 'none', padding: '6px 0', cursor: 'pointer', color: 'var(--c-text-fg)' }}>
              <strong>{d.name}</strong> <small>({d.versions})</small></button></li>
          ))}
        </ul>
        <div style={{ flex: '1 1 560px', minWidth: 0 }}>
          {selected && (
            <div data-testid="dataset-detail">
              <h3 style={{ marginTop: 0 }}>{selected.name}</h3>
              <section style={box}>
                <h4 style={{ marginTop: 0 }}>{t('buildVersion')}</h4>
                <p style={{ fontSize: 13, color: 'var(--c-text-muted-fg)' }}>{t('buildHelp')}</p>
                <fieldset style={{ border: '1px solid var(--c-border)', borderRadius: 6, marginBottom: 8 }}
                  data-testid="build-uploads">
                  <legend style={{ fontSize: 13 }}>{t('source_uploads')}</legend>
                  {uploads.length === 0 && <p style={{ fontSize: 13, margin: 0 }}>{t('noUploads')}</p>}
                  {uploads.map((u) => (
                    <label key={u.upload_id} style={{ display: 'flex', gap: 6, fontSize: 13, alignItems: 'center' }}
                      title={t('includeUploadTip')}>
                      <input type="checkbox" checked={chosenUploads.includes(u.upload_id)}
                        data-testid={`build-upload-${u.upload_id}`} onChange={() => toggleUpload(u.upload_id)} />
                      <span>{t('uploadRow', { n: u.items, format: u.format, date: (u.created_at || '').slice(0, 10) })}
                        {' '}<code>{u.sidecar_sha256.slice(0, 10)}…</code></span>
                    </label>
                  ))}
                </fieldset>
                <WriteForm t={t} testId="build-form" submitLabel="build" onSubmit={build}
                  fields={[{ name: 'expert', label: 'source_expert', type: 'checkbox', initial: true },
                    { name: 'ai_review', label: 'source_ai_review', type: 'checkbox', initial: false },
                    { name: 'rank', options: ['species', 'genus'], initial: 'species' },
                    { name: 'min_agreement', initial: '0.67', size: 4 }, { name: 'min_per_class', initial: '5', size: 4 },
                    { name: 'val_fraction', initial: '0.15', size: 4 }, { name: 'test_fraction', initial: '0.15', size: 4 },
                    { name: 'split_seed', initial: '17', size: 4 }, { name: 'reason' }]} />
              </section>
              {selected.versions.map((v) => (
                <section key={v.version_id} style={box} data-testid={`version-${v.version}`}>
                  <h4 style={{ marginTop: 0 }}>v{v.version}
                    <span style={badge(v.status === 'frozen' ? 'success' : v.status === 'failed' ? 'danger' : 'warning')}
                      data-testid={`version-status-${v.version}`}>{t(`status_${v.status}`)}</span></h4>
                  {v.error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{v.error}</p>}
                  {v.item_count !== null && <p style={{ fontSize: 13 }}>{t('items', { n: v.item_count })}
                    {v.report && ` · ${t('duplicates', { n: v.report.duplicates })} · ${t('disagreed', { n: v.report.disagreed })}`}
                    {v.report && Object.keys(v.report.dropped_classes || {}).length > 0
                      && ` · ${t('droppedClasses', { n: Object.keys(v.report.dropped_classes).length })}`}</p>}
                  {v.classes && (
                    <table style={{ fontSize: 13, width: '100%' }} data-testid={`classes-${v.version}`}>
                      <thead><tr><th style={{ textAlign: 'start' }}>{t('class')}</th><th>{t('train')}</th><th>{t('val')}</th><th>{t('test')}</th></tr></thead>
                      <tbody>{Object.entries(v.classes).map(([c, n]) => (
                        <tr key={c}><td>{c}</td><td>{n.train}</td><td>{n.val}</td><td>{n.test}</td></tr>))}</tbody>
                    </table>
                  )}
                  {v.manifest_sha256 && <p style={{ fontSize: 12 }}>{t('manifest')}: <code>{v.manifest_sha256.slice(0, 16)}…</code></p>}
                  {v.status === 'built' && (
                    <WriteForm t={t} testId={`freeze-${v.version}`} submitLabel="freeze" onSubmit={freeze(v.version_id)}
                      fields={[{ name: 'reason' }]} />
                  )}
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Uploads({ t }) {
  const [result, setResult] = useState(null);
  const submit = async (v) => { setResult((await api.post('/mlops/uploads', v)).data); };
  return (
    <section style={box}>
      <h4 style={{ marginTop: 0 }}>{t('uploadSidecar')}</h4>
      <p style={{ fontSize: 13, color: 'var(--c-text-muted-fg)' }}>{t('uploadHelp')}</p>
      <WriteForm t={t} testId="upload-form" submitLabel="upload" onSubmit={submit}
        fields={[{ name: 'format', options: ['json', 'yaml'], initial: 'json' }, { name: 'sidecar', type: 'textarea' },
          { name: 'reason' }]} />
      {result && <p data-testid="upload-result">{t('uploaded', { n: result.items })} <code>{result.upload_id}</code></p>}
    </section>
  );
}

function Training({ t, versions }) {
  const [runs, setRuns] = useState([]);
  const [error, setError] = useState('');
  const load = useCallback(() => api.get('/mlops/training-runs').then(({ data }) => setRuns(data))
    .catch((e) => setError(errorText(e))), []);
  useEffect(() => { load(); }, [load]);
  const start = async (v) => {
    await api.post('/mlops/training-runs', { dataset_version_id: v.dataset_version_id, reason: v.reason,
      params: { epochs: Number(v.epochs), backbone: v.backbone, learning_rate: Number(v.learning_rate),
        unfreeze_last_stage: !!v.unfreeze_last_stage } });
    await load();
  };
  return (
    <div>
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      <section style={box}>
        <h4 style={{ marginTop: 0 }}>{t('startRun')}</h4>
        <p style={{ fontSize: 13, color: 'var(--c-text-muted-fg)' }}>{t('runHelp')}</p>
        <WriteForm t={t} testId="run-form" submitLabel="start" onSubmit={start}
          fields={[{ name: 'dataset_version_id', label: 'frozenVersion', initial: versions[0]?.value || '',
            options: [{ value: '', label: '—' }, ...versions] },
          { name: 'epochs', initial: '8', size: 4 },
          { name: 'backbone', initial: 'convnext_base.fb_in22k_ft_in1k',
            options: [{ value: 'convnext_base.fb_in22k_ft_in1k', label: 'ConvNeXt-B (IN-22k)' },
              { value: 'vit_base_patch14_dinov2.lvd142m', label: 'ViT-B/14 DINOv2' }] },
          { name: 'learning_rate', initial: '0.001', size: 6 },
          { name: 'unfreeze_last_stage', type: 'checkbox', initial: true }, { name: 'reason' }]} />
      </section>
      <table style={{ width: '100%', fontSize: 13 }} data-testid="runs">
        <thead><tr>{['created', 'status', 'params', 'evaluation', 'model'].map((c) => <th key={c} style={{ textAlign: 'start' }}>{t(`col_${c}`)}</th>)}</tr></thead>
        <tbody>{runs.map((r) => {
          const ev = r.metrics?.evaluation;
          return (
            <tr key={r.run_id}>
              <td>{new Date(r.created_at).toLocaleString()}</td>
              <td><span style={badge(RUN_TONE[r.status])}>{t(`run_${r.status}`)}</span>{r.error && <small> {r.error}</small>}
                {r.status === 'queued' && <div style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>{t('queuedHint')}</div>}</td>
              <td>{r.params.backbone?.split('.')[0]} · {r.params.epochs} ep</td>
              <td>{ev ? `top-1 ${pct(ev.top1)} · F1 ${pct(ev.macro_f1)} · ECE ${pct(ev.ece)}` : '—'}</td>
              <td><code>{r.produced_model_version || '—'}</code></td>
            </tr>
          );
        })}</tbody>
      </table>
    </div>
  );
}

function Models({ t }) {
  const mayRun = useContext(MayRun);
  const [models, setModels] = useState([]);
  const [history, setHistory] = useState([]);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [gate, setGate] = useState(null);
  const load = useCallback(async () => {
    try {
      const [m, h] = await Promise.all([api.get('/mlops/models'), api.get('/mlops/promotions')]);
      setModels(m.data);
      setHistory(h.data);
    } catch (e) {
      setError(errorText(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const production = models.find((m) => m.stage === 'production');
  const act = async (path) => {
    setError('');
    setGate(null);
    try {
      await api.post(path, { reason });
      setReason('');
      await load();
    } catch (e) {
      setError(errorText(e));
      if (e?.response?.data?.failures) setGate(e.response.data.failures);
    }
  };
  // The gate compares frozen-test evaluations only; a model measured only on the
  // held-out reference split (migration 0068) shows those numbers, labelled.
  const frozenOf = (m) => (m.evaluations || []).filter((e) => (e.source || 'frozen_test') === 'frozen_test');
  const evalOn = (m, versionId) => frozenOf(m).find((e) => e.dataset_version_id === versionId);
  const canPromote = (m) => m.stage === 'candidate' || (m.stage === 'archived' && !production);
  return (
    <div>
      {/* What is actually serving, beside what the registry says is production. */}
      <ServedModelCard />
      {mayRun && <label style={label}>{t('reason')}
        <input style={input} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="model-reason"
          title={t('reasonTip')} />
      </label>}
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      {gate && (
        <ul data-testid="gate-failures" style={{ color: 'var(--c-danger-fg)', fontSize: 13 }}>
          {gate.map((f) => <li key={f.metric}>{t(`metric_${f.metric}`)}: {pct(f.candidate)} vs {pct(f.production)}</li>)}
        </ul>
      )}
      <table style={{ width: '100%', fontSize: 13 }} data-testid="models">
        <thead><tr>{['model', 'stage', 'top1', 'macro_f1', 'ece', 'gate', 'actions'].map((c) => <th key={c} style={{ textAlign: 'start' }}>{t(`col_${c}`)}</th>)}</tr></thead>
        <tbody>{models.map((m) => {
          const frozen = frozenOf(m)[0];
          const ev = frozen || (m.evaluations || [])[0];
          const reference = ev && ev.source === 'reference_holdout';
          const prodEv = production && frozen && evalOn(production, frozen.dataset_version_id);
          const cmp = (metric, higher) => (prodEv && m !== production
            ? ((higher ? ev[metric] >= prodEv[metric] : ev[metric] <= prodEv[metric]) ? ' ✓' : ' ✗') : '');
          return (
            <tr key={m.model_version} data-testid={`model-${m.model_version}`}>
              <td><code>{m.model_version}</code>
                {reference && (
                  <div style={{ fontSize: 11, color: 'var(--c-text-muted-fg)' }} data-testid={`model-reference-${m.model_version}`}>
                    {t('evaluationReference', { n: ev.n, top5: pct(ev.top5) })}
                  </div>
                )}</td>
              <td><span style={badge(STAGE_TONE[m.stage])}>{t(`stage_${m.stage}`)}</span></td>
              <td>{ev ? pct(ev.top1) + cmp('top1', true) : '—'}</td>
              <td>{ev && ev.macro_f1 != null ? pct(ev.macro_f1) + cmp('macro_f1', true) : '—'}</td>
              <td>{ev ? pct(ev.ece) + cmp('ece', false) : '—'}</td>
              <td data-testid={`gate-${m.model_version}`}>{!canPromote(m) ? '—'
                : !production ? t('gateNoProduction')
                  : !frozen ? t('gateNoEvaluation')
                    : !prodEv ? t('gateNotComparable')
                      : [cmp('top1', true), cmp('macro_f1', true), cmp('ece', false)].every((x) => x === ' ✓')
                        ? t('gateWouldPass') : t('gateWouldFail')}</td>
              <td>{mayRun && canPromote(m) && (
                <>
                  <button type="button" data-testid={`promote-${m.model_version}`}
                    title={m.stage === 'archived' ? t('promoteServedTip') : t('promoteTip')}
                    onClick={() => act(`/mlops/models/${encodeURIComponent(m.model_version)}/promote`)}>{t('promote')}</button>{' '}
                  {m.stage === 'candidate' && (
                    <button type="button" title={t('archiveTip')}
                      onClick={() => act(`/mlops/models/${encodeURIComponent(m.model_version)}/archive`)}>{t('archive')}</button>
                  )}
                </>
              )}</td>
            </tr>
          );
        })}
        {models.length === 0 && (
          <tr><td colSpan={7} data-testid="models-empty">{t('modelsEmpty')}</td></tr>
        )}</tbody>
      </table>
      <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>{t('gateHelp')}</p>
      <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }} data-testid="serving-note">{t('servingNote')}</p>
      {mayRun && production && (
        <button type="button" data-testid="rollback" title={t('rollbackTip')} onClick={() => act('/mlops/models/rollback')}>{t('rollback')}</button>
      )}
      <h4>{t('history')}</h4>
      <ul style={{ fontSize: 13 }} data-testid="promotions">{history.map((h, i) => (
        <li key={i}>{new Date(h.at).toLocaleString()} · <code>{h.model_version}</code> {t(`stage_${h.from_stage}`)} → {t(`stage_${h.to_stage}`)} · {h.by || t('system')}: {h.reason}</li>
      ))}</ul>
    </div>
  );
}

/*
 * The prediction log (GET /ai/identifications): every identification the
 * service answered, newest first -- which version, who asked, its top answer,
 * whether it was routed to review and why, how long it took, how many people
 * gave feedback. Filtered by version and routing, paged.
 */
export const LOG_PAGE = 50;

function Identifications({ t }) {
  const [filters, setFilters] = useState({ model_version: '', needs_review: '' });
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    const params = { limit: LOG_PAGE, offset };
    if (filters.model_version) params.model_version = filters.model_version;
    if (filters.needs_review) params.needs_review = filters.needs_review;
    api.get('/ai/identifications', { params })
      .then(({ data: body }) => { if (live) { setData(body || {}); setError(''); } })
      .catch((e) => { if (live) setError(errorText(e)); });
    return () => { live = false; };
  }, [filters, offset]);
  const set = (key) => (e) => { setOffset(0); setFilters((f) => ({ ...f, [key]: e.target.value })); };
  const items = data?.items || [];
  const total = data?.total || 0;
  return (
    <section data-testid="identifications">
      <p style={{ fontSize: 13, color: 'var(--c-text-muted-fg)', marginTop: 0 }}>{t('log.intro')}</p>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <label style={label}>{t('log.version')}
          <select style={input} value={filters.model_version} onChange={set('model_version')}
            data-testid="log-version" title={t('log.versionTip')}>
            <option value="">{t('log.allVersions')}</option>
            {(data?.model_versions || []).map((v) => (
              <option key={v.model_version} value={v.model_version}>{v.model_version} ({v.count})</option>
            ))}
          </select>
        </label>
        <label style={label}>{t('log.routing')}
          <select style={input} value={filters.needs_review} onChange={set('needs_review')}
            data-testid="log-routing" title={t('log.routingTip')}>
            <option value="">{t('log.all')}</option>
            <option value="true">{t('log.routed')}</option>
            <option value="false">{t('log.notRouted')}</option>
          </select>
        </label>
      </div>
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      {!data && !error && <p>{t('pipeline.loading')}</p>}
      {data && items.length === 0 && <p data-testid="log-empty">{t('log.empty')}</p>}
      {items.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 13 }} data-testid="log-table">
            <thead><tr>{['when', 'photo', 'answer', 'review', 'model', 'by', 'ms', 'feedback'].map((c) => (
              <th key={c} style={{ textAlign: 'start' }}>{t(`log.col_${c}`)}</th>))}</tr></thead>
            <tbody>{items.map((i) => (
              <tr key={i.ai_id} data-testid={`log-${i.ai_id}`}>
                <td>{i.created_at ? new Date(i.created_at).toLocaleString() : '—'}</td>
                <td>{i.image_url
                  ? <a href={resolvePhotoUrl(i.image_url)} target="_blank" rel="noreferrer" title={t('log.photoTip')}>
                    <img src={resolvePhotoUrl(i.image_url)} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }} /></a>
                  : '—'}</td>
                <td><span dir="ltr" style={{ fontStyle: 'italic' }}>{i.top_taxon || '—'}</span>
                  {i.top_confidence != null && ` ${pct(i.top_confidence)}`}
                  {i.toxicity_flag && <> <span style={badge('danger')}>{t('log.toxic')}</span></>}</td>
                <td>{i.needs_review
                  ? <span style={badge('warning')} title={(i.review_reasons || []).map((r) => t(`log.reason_${r}`)).join(', ')}>
                    {t('log.routed')}</span>
                  : t('log.notRouted')}</td>
                <td><code>{i.model_version}</code></td>
                <td>{i.requested_by || '—'}</td>
                <td>{i.inference_ms ?? '—'}</td>
                <td>{i.feedback_count}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {total > LOG_PAGE && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
          <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LOG_PAGE))}
            data-testid="log-prev" title={t('log.prevTip')}>{t('log.prev')}</button>
          <span style={{ fontSize: 13 }} data-testid="log-page">
            {t('log.range', { from: offset + 1, to: Math.min(offset + LOG_PAGE, total), total })}</span>
          <button type="button" disabled={offset + LOG_PAGE >= total} onClick={() => setOffset(offset + LOG_PAGE)}
            data-testid="log-next" title={t('log.nextTip')}>{t('log.next')}</button>
        </div>
      )}
    </section>
  );
}

export default function Mlops() {
  const { t } = useTranslation('mlops');
  const { user } = useAuth();
  const mayRead = can(user, 'mlops:read');
  const mayRun = can(user, 'mlops:admin');
  const [tab, setTab] = useState('datasets');
  const [versions, setVersions] = useState([]);
  useEffect(() => {
    if (tab !== 'training' || !mayRead) return;
    api.get('/mlops/datasets').then(async ({ data }) => {
      const details = await Promise.all(data.map((d) => api.get(`/mlops/datasets/${d.dataset_id}`)));
      setVersions(details.flatMap(({ data: d }) => d.versions.filter((v) => v.status === 'frozen')
        .map((v) => ({ value: v.version_id, label: `${d.name} v${v.version}` }))));
    }).catch(() => setVersions([]));
  }, [tab, mayRead]);
  if (!mayRead) return <p style={{ color: 'var(--c-text-muted-fg)' }} data-testid="mlops-restricted">{t('restricted')}</p>;
  return (
    <MayRun.Provider value={mayRun}>
    <div>
      <h2>{t('pageTitle')}</h2>
      <p style={{ color: 'var(--c-text-muted-fg)', marginTop: 0 }}>{t('intro')}</p>
      <Pipeline t={t} onOpen={setTab} />
      <div role="tablist" style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {['datasets', 'uploads', 'training', 'models', 'metrics', 'identifications'].map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} data-testid={`tab-${k}`}
            title={t(`tabTip_${k}`)}
            onClick={() => setTab(k)} style={{ fontWeight: tab === k ? 600 : 400 }}>{t(`tab_${k}`)}</button>
        ))}
      </div>
      {tab === 'datasets' && <Datasets t={t} />}
      {tab === 'uploads' && <Uploads t={t} />}
      {tab === 'training' && <Training t={t} versions={versions} key={versions.length} />}
      {tab === 'models' && <Models t={t} />}
      {tab === 'metrics' && (
        <>
          <AiModelDashboard />
          {/* The certainty thresholds and their measured precision, here too: a
              system administrator cannot open the expert review page. */}
          <AiThresholdsPanel />
        </>
      )}
      {tab === 'identifications' && <Identifications t={t} />}
    </div>
    </MayRun.Provider>
  );
}
